import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Briefcase, Search, Users, Leaf, MapPin, ChevronDown, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  heroData,
  aboutData,
  servicesData,
  vakgebiedenData,
  timelineData,
  vacaturesData,
  faqData,
  faqDataPublic,
  clientLogos,
  statsData,
  metricsTable,
} from '../data/mockData';
import { specialismes } from '../data/specialismes';

const FadeInSection = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Hero ─── */
const HeroSection = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={heroRef} className="relative h-screen overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        {/* Mobile: static image only (no video) for fast LCP on cellular */}
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&auto=format&fit=crop"
          alt="Modern kantoor in Amsterdam – Staffing Force, experts in Finance & HR"
          className="hero-video md:hidden"
          fetchpriority="high"
        />
        {/* Desktop: background video with poster fallback */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&auto=format&fit=crop"
          aria-label="Achtergrondvideo van een modern kantoor – Staffing Force Amsterdam"
          className="hero-video hidden md:block"
        >
          <source src={heroData.videoUrl} type="video/mp4" />
        </video>
      </motion.div>
      <div className="hero-overlay absolute inset-0 z-10" />
      <motion.div style={{ opacity }} className="relative z-20 h-full flex flex-col justify-center items-center px-4 md:px-6">
        <div className="text-center max-w-5xl w-full">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="font-playfair italic text-white/90 text-base sm:text-xl md:text-2xl mb-3 md:mb-4 tracking-wide">
            {heroData.subtitle}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="text-[2.5rem] sm:text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[1.05] tracking-tight whitespace-pre-line">
            {heroData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.0 }}
            className="text-white/80 text-sm sm:text-base md:text-xl mt-5 md:mt-6 max-w-2xl mx-auto px-2">
            {heroData.description}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.3 }}
            className="flex flex-col sm:flex-row gap-3 mt-8 md:mt-10 justify-center px-4 sm:px-0">
            <Link to="/vacatures"
              className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#00897B]/30 hover:-translate-y-0.5 flex items-center justify-center gap-2 min-h-[48px]">
              {heroData.ctaPrimary} <ArrowRight size={16} />
            </Link>
            <Link to="/contact"
              className="border-2 border-white/40 hover:border-white text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2 min-h-[48px]">
              {heroData.ctaSecondary}
            </Link>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
          className="absolute bottom-28 md:bottom-24 left-1/2 -translate-x-1/2 hidden md:block">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <ChevronDown size={28} className="text-white/60" />
          </motion.div>
        </motion.div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-0 left-0 right-0 z-20 bg-[#0d1b2a]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
          <span className="text-white/70 text-xs md:text-sm whitespace-nowrap">Hoe kan ik u helpen?</span>
          <div className="flex flex-wrap gap-2 w-full overflow-x-auto">
            {[
              { label: 'Ik zoek een vacature', to: '/vacatures' },
              { label: 'Ik zoek contact over', to: '/contact' },
              { label: 'Ik zoek een professional', to: '/opdracht-opgeven' },
              { label: 'Bekijk alle vacatures', to: '/vacatures' },
            ].map(({ label, to }) => (
              <Link key={label} to={to} className="px-3 md:px-4 py-1.5 md:py-2 border border-white/20 rounded text-white/80 text-xs hover:bg-white/10 transition-colors whitespace-nowrap">{label}</Link>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

/* ─── Stats ─── */
const StatsSection = () => (
  <section className="py-12 md:py-16 bg-[#00897B]">
    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {statsData.map((s, i) => (
          <FadeInSection key={i} delay={i * 0.1}>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-1 md:mb-2">{s.number}</p>
              <p className="text-white/80 text-xs sm:text-sm font-medium">{s.label}</p>
            </div>
          </FadeInSection>
        ))}
      </div>
    </div>
  </section>
);

/* ─── About + Timeline ─── */
const AboutSection = () => (
  <section id="over-ons" className="py-16 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
      {/* About text + image */}
      <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center mb-16 md:mb-24">
        <FadeInSection>
          <div>
            <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">{aboutData.subtitle}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-5 md:mb-6 leading-tight">{aboutData.title}</h2>
            <p className="text-gray-600 leading-relaxed mb-5 md:mb-6 text-sm md:text-base">{aboutData.description}</p>
            <p className="text-gray-600 leading-relaxed mb-5 md:mb-6 text-sm md:text-base">{aboutData.mission}</p>
            <div className="bg-[#00897B]/5 border-l-4 border-[#00897B] p-4 rounded-r-lg">
              <p className="text-[#00897B] font-medium text-sm">{aboutData.highlight}</p>
            </div>
          </div>
        </FadeInSection>
        <FadeInSection delay={0.2}>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={aboutData.image} alt="Finance en HR professionals in overleg – Staffing Force Amsterdam" className="w-full h-[300px] md:h-[450px] object-cover" loading="lazy" width="800" height="450" />
            </div>
            <div className="absolute -bottom-6 -left-4 md:-left-6 bg-[#00897B] text-white p-4 md:p-6 rounded-xl shadow-xl">
              <p className="text-2xl md:text-3xl font-bold">18+</p>
              <p className="text-xs md:text-sm text-white/80">Jaar ervaring</p>
            </div>
          </div>
        </FadeInSection>
      </div>

      {/* Timeline */}
      <FadeInSection>
        <div className="text-center mb-10 md:mb-16">
          <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">Ons verhaal</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-3">Historie</h2>
        </div>
      </FadeInSection>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#00897B]/20 hidden lg:block" />

        <div className="space-y-12 lg:space-y-0">
          {timelineData.map((item, index) => {
            const isLeft = index % 2 === 0;
            return (
              <FadeInSection key={item.year} delay={index * 0.1}>
                <div className={`lg:flex items-center lg:mb-16 ${isLeft ? '' : 'lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={`lg:w-[calc(50%-2rem)] ${isLeft ? 'lg:pr-8 lg:text-right' : 'lg:pl-8'}`}>
                    <div className={`bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow ${isLeft ? '' : ''}`}>
                      <span className="text-[#00897B] font-bold text-2xl">{item.year}</span>
                      <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="hidden lg:flex w-16 justify-center flex-shrink-0">
                    <div className="w-4 h-4 rounded-full bg-[#00897B] border-4 border-white shadow-md" />
                  </div>

                  {/* Spacer for opposite side */}
                  <div className="lg:w-[calc(50%-2rem)] hidden lg:block" />
                </div>
              </FadeInSection>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* ─── Vakgebieden (YER-style) ─── */
const VakgebiedenSection = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -360 : 360;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
      setTimeout(checkScroll, 400);
    }
  };

  return (
    <section className="py-24 bg-[#0d1b2a] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#00897B]/5 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeInSection>
          <div className="text-center mb-14">
            <span className="text-[#00bfa5] text-sm font-semibold tracking-widest uppercase">Onze expertises</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 font-playfair italic">
              Wij zijn actief in jouw vakgebied
            </h2>
          </div>
        </FadeInSection>

        {/* Scrollable cards */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {vakgebiedenData.map((vak, index) => (
            <FadeInSection key={vak.id} delay={index * 0.1}>
              <Link
                to="/vacatures"
                className="group flex-shrink-0 w-[300px] md:w-[340px] rounded-xl overflow-hidden snap-start relative block"
              >
                <div className="h-[400px] overflow-hidden relative">
                  <img
                    src={vak.image}
                    alt={vak.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Dark overlay - stronger on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500" />
                  
                  {/* Content overlay - title + info visible on hover */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <h3 className="text-2xl font-bold text-white mb-2 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      {vak.title}
                    </h3>
                    <p className="text-white/0 group-hover:text-white/80 text-sm leading-relaxed transition-all duration-500 max-h-0 group-hover:max-h-24 overflow-hidden">
                      {vak.description}
                    </p>
                    <div className="flex items-center justify-between mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="text-[#00bfa5] text-sm font-medium">{vak.vacatures} vacatures</span>
                      <ArrowRight size={16} className="text-white group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </FadeInSection>
          ))}
        </div>

        {/* Navigation controls */}
        <div className="flex items-center justify-center gap-6 mt-10">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
              canScrollLeft ? 'border-white/40 text-white hover:border-white' : 'border-white/10 text-white/20 cursor-not-allowed'
            }`}
          >
            <ChevronLeft size={20} />
          </button>
          <Link
            to="/vacatures"
            className="px-8 py-3 border-2 border-white/40 text-white rounded font-semibold text-sm hover:border-white transition-all hover:-translate-y-0.5"
          >
            Alle vakgebieden
          </Link>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
              canScrollRight ? 'border-white/40 text-white hover:border-white' : 'border-white/10 text-white/20 cursor-not-allowed'
            }`}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── Specialismen (GEO landing pages) ─── */
const SpecialismenSection = () => {
  const items = Object.values(specialismes);
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <FadeInSection>
          <div className="text-center mb-10 md:mb-14 max-w-3xl mx-auto">
            <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">Specialismen</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-3">Direct naar de juiste rol</h2>
            <p className="text-gray-600 mt-4 text-sm md:text-base">Tarieven, doorlooptijden en praktijkvoorbeelden per specialisme — Finance, HR en Duurzaamheid.</p>
          </div>
        </FadeInSection>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {items.map((spec, i) => (
            <FadeInSection key={spec.slug} delay={i * 0.08}>
              <Link
                to={`/specialisme/${spec.slug}`}
                className="group block bg-gray-50 hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/30 rounded-2xl p-6 md:p-7 transition-all duration-300 h-full"
              >
                <span className="inline-block text-[#00897B] text-xs font-semibold tracking-widest uppercase mb-3">
                  {spec.discipline}
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 leading-tight">
                  {spec.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  {spec.intro.slice(0, 140)}…
                </p>
                <div className="flex items-center gap-2 text-[#00897B] font-semibold text-sm group-hover:gap-3 transition-all">
                  Bekijk specialisme <ArrowRight size={16} />
                </div>
              </Link>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Vacatures Preview (split layout) ─── */
const VacaturesPreview = () => {
  const recentVacatures = vacaturesData.slice(0, 5);
  const [activeId, setActiveId] = useState(recentVacatures[0]?.id ?? null);
  const active = recentVacatures.find((v) => v.id === activeId) || recentVacatures[0];

  const disciplineColors = {
    Finance: 'bg-[#00897B] text-white',
    HR: 'bg-[#0d1b2a] text-white',
    Duurzaamheid: 'bg-[#2e7d32] text-white',
  };

  const previewBenefits = [
    'Marktconform salaris',
    'Persoonlijke begeleiding',
    'Doorgroei- en opleidingsmogelijkheden',
    'Werken bij toonaangevende organisaties',
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <FadeInSection>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 md:mb-12 gap-4">
            <div>
              <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">Recente Vacatures</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-3">Openstaande posities</h2>
            </div>
            <Link to="/vacatures" className="flex items-center gap-2 text-[#00897B] font-semibold hover:gap-3 transition-all duration-300 text-sm min-h-[44px]">
              Alle vacatures bekijken <ArrowRight size={16} />
            </Link>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: list */}
          <div className="lg:col-span-3 space-y-3">
            {recentVacatures.map((vac, i) => {
              const isActive = active?.id === vac.id;
              return (
                <FadeInSection key={vac.id} delay={i * 0.06}>
                  <div
                    onMouseEnter={() => setActiveId(vac.id)}
                    onClick={() => setActiveId(vac.id)}
                    className={`cursor-pointer bg-white rounded-xl p-5 flex items-start gap-4 transition-all border ${
                      isActive
                        ? 'border-[#00897B] shadow-lg shadow-[#00897B]/10 -translate-y-0.5'
                        : 'border-gray-100 hover:border-[#00897B]/40'
                    }`}
                    data-testid={`home-vacature-card-${vac.id}`}
                  >
                    <div
                      className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                        disciplineColors[vac.discipline] || 'bg-gray-800 text-white'
                      }`}
                    >
                      {vac.discipline.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <h3
                          className={`text-[15px] font-semibold leading-snug transition-colors ${
                            isActive ? 'text-[#00897B]' : 'text-gray-900'
                          }`}
                        >
                          {vac.title}
                        </h3>
                        <ArrowUpRight
                          size={16}
                          className={`flex-shrink-0 mt-0.5 transition-colors ${
                            isActive ? 'text-[#00897B]' : 'text-gray-300'
                          }`}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={11} className="text-[#00897B]" /> {vac.location}
                        </span>
                        <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                        <span>{vac.type}</span>
                        <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                        <span>{vac.level}</span>
                      </div>
                    </div>
                  </div>
                </FadeInSection>
              );
            })}
          </div>

          {/* Right: sticky preview card */}
          <div className="lg:col-span-2">
            <FadeInSection delay={0.2}>
              <div className="lg:sticky lg:top-24">
                <motion.div
                  key={active?.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="bg-gradient-to-br from-[#0d1b2a] to-[#00897B] rounded-2xl p-7 text-white shadow-xl overflow-hidden relative"
                  data-testid="home-vacature-preview"
                >
                  <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#00bfa5]/20 rounded-full blur-3xl" />
                  <div className="relative">
                    <span className="inline-block text-[11px] font-semibold tracking-widest uppercase text-[#00bfa5] mb-3">
                      {active?.discipline} • {active?.type}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-4">
                      {active?.title}
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed mb-6">
                      {active?.description}
                    </p>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="bg-white/10 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Locatie</p>
                        <p className="text-sm font-medium flex items-center gap-1">
                          <MapPin size={12} className="text-[#00bfa5]" /> {active?.location}
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Niveau</p>
                        <p className="text-sm font-medium">{active?.level}</p>
                      </div>
                    </div>

                    <div className="border-t border-white/15 pt-5 mb-5">
                      <p className="text-[10px] uppercase tracking-wider text-white/60 mb-3">Wat wij bieden</p>
                      <ul className="space-y-2">
                        {previewBenefits.map((b, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00bfa5] flex-shrink-0" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to={`/vacatures/${active?.id}`}
                      className="flex items-center justify-center gap-2 w-full bg-white text-[#00897B] hover:bg-[#00bfa5] hover:text-white px-5 py-3 rounded-lg text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5"
                      data-testid="home-vacature-cta"
                    >
                      Bekijk vacature <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Clients ─── */
const ClientsSection = () => (
  <section className="py-20 bg-gray-50 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
      <FadeInSection>
        <div className="text-center">
          <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Vertrouwd door</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Onze klanten</h2>
        </div>
      </FadeInSection>
    </div>
    <div className="relative">
      <div className="flex animate-marquee">
        {[...clientLogos, ...clientLogos, ...clientLogos].map((logo, i) => (
          <div key={i} className="flex-shrink-0 mx-8 flex items-center justify-center">
            <img src={logo.url} alt={logo.name} className="h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-500 opacity-60 hover:opacity-100" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── FAQ ─── */
const FAQSection = () => (
  <section className="py-16 md:py-24 bg-white">
    <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
      <FadeInSection>
        <div className="text-center mb-10 md:mb-16">
          <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">Veelgestelde vragen</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-3">FAQ — Detachering Finance, HR en Duurzaamheid</h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">De meestgestelde vragen van opdrachtgevers en kandidaten over interim, detavast en werving & selectie in Amsterdam en heel Nederland.</p>
        </div>
      </FadeInSection>
      <FadeInSection delay={0.2}>
        <Accordion type="single" collapsible className="space-y-3">
          {faqDataPublic.slice(0, 8).map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`}
              className="bg-gray-50 border border-gray-100 rounded-xl px-4 md:px-6 overflow-hidden data-[state=open]:bg-[#00897B]/5 data-[state=open]:border-[#00897B]/20 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#00897B] transition-colors py-5 text-sm md:text-base hover:no-underline">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5 text-sm">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-10">
          <Link to="/veelgestelde-vragen" className="inline-flex items-center gap-2 text-[#00897B] font-semibold hover:gap-3 transition-all text-sm">
            Alle veelgestelde vragen bekijken <ArrowRight size={16} />
          </Link>
        </div>
      </FadeInSection>
    </div>
  </section>
);

/* ─── CTA ─── */
const CTASection = () => (
  <section className="py-16 md:py-24 bg-[#0d1b2a] relative overflow-hidden">
    <div className="absolute top-0 right-0 w-96 h-96 bg-[#00897B]/10 rounded-full blur-3xl" />
    <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#00897B]/5 rounded-full blur-3xl" />
    <div className="relative max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
      <FadeInSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5 md:mb-6 leading-tight">Klaar om de volgende stap te zetten?</h2>
        <p className="text-white/70 text-sm md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto">Of u nu op zoek bent naar de perfecte kandidaat of uw volgende carrièrestap wilt maken, wij helpen u graag verder.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/vacatures" className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#00897B]/30 hover:-translate-y-0.5 min-h-[48px] inline-flex items-center justify-center">
            Bekijk vacatures
          </Link>
          <Link to="/contact" className="border-2 border-white/30 hover:border-white text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 min-h-[48px] inline-flex items-center justify-center">
            Neem contact op
          </Link>
        </div>
      </FadeInSection>
    </div>
  </section>
);

/* ─── Page ─── */
const HomePage = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqDataPublic.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <main>
      <SEO />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <HeroSection />
      <StatsSection />
      <AboutSection />
      <VakgebiedenSection />
      <SpecialismenSection />
      <VacaturesPreview />
      <ClientsSection />
      <FAQSection />
      <CTASection />
    </main>
  );
};

export default HomePage;
